import { defineStore } from 'pinia'
import { ref } from 'vue'
import { localTourProgressSync, type TourProgressSnapshot } from '../services/tour-progress.service'
import { TOUR_STORAGE_KEY } from '../config/tour.config'

/**
 * Persisted tour progress. Mirrors the onboarding.store pattern: a small, flat,
 * localStorage-persisted slice. The remote sync boundary (`localTourProgressSync`)
 * is called best-effort after mutations so a backend can be added later.
 */
export const useTourProgressStore = defineStore(
  'tourProgress',
  () => {
    const completedTourIds = ref<string[]>([])
    const skippedTourIds = ref<string[]>([])
    const dismissedTourIds = ref<string[]>([]) // "don't show again"
    const stepIndexByTour = ref<Record<string, number>>({})
    /** When true, un-seen tours auto-start on every screen; false = only via help button. */
    const trainingModeAllScreens = ref(true)

    const sync = localTourProgressSync

    function snapshot(): TourProgressSnapshot {
      return {
        completedTourIds: [...completedTourIds.value],
        skippedTourIds: [...skippedTourIds.value],
        dismissedTourIds: [...dismissedTourIds.value],
        stepIndexByTour: { ...stepIndexByTour.value },
      }
    }

    function pushSync(): void {
      sync.push(snapshot())
    }

    function addUnique(list: string[], id: string): void {
      if (!list.includes(id)) list.push(id)
    }

    function isCompleted(id: string): boolean {
      return completedTourIds.value.includes(id)
    }
    function isSkipped(id: string): boolean {
      return skippedTourIds.value.includes(id)
    }
    function isDismissed(id: string): boolean {
      return dismissedTourIds.value.includes(id)
    }
    /** Has the user already engaged (finished / skipped / dismissed) this tour? */
    function hasEngaged(id: string): boolean {
      return isCompleted(id) || isSkipped(id) || isDismissed(id)
    }
    function lastStepIndex(id: string): number {
      return stepIndexByTour.value[id] ?? 0
    }

    function setStepIndex(id: string, index: number): void {
      stepIndexByTour.value[id] = index
      pushSync()
    }

    function markCompleted(id: string): void {
      addUnique(completedTourIds.value, id)
      // Completing clears a prior skip so progress reflects reality.
      skippedTourIds.value = skippedTourIds.value.filter((t) => t !== id)
      pushSync()
    }

    function markSkipped(id: string): void {
      if (!isCompleted(id)) addUnique(skippedTourIds.value, id)
      pushSync()
    }

    function dismissForever(id: string): void {
      addUnique(dismissedTourIds.value, id)
      pushSync()
    }

    function resetTour(id: string): void {
      completedTourIds.value = completedTourIds.value.filter((t) => t !== id)
      skippedTourIds.value = skippedTourIds.value.filter((t) => t !== id)
      dismissedTourIds.value = dismissedTourIds.value.filter((t) => t !== id)
      delete stepIndexByTour.value[id]
      pushSync()
    }

    function $reset(): void {
      completedTourIds.value = []
      skippedTourIds.value = []
      dismissedTourIds.value = []
      stepIndexByTour.value = {}
      trainingModeAllScreens.value = true
      pushSync()
    }

    return {
      completedTourIds,
      skippedTourIds,
      dismissedTourIds,
      stepIndexByTour,
      trainingModeAllScreens,
      isCompleted,
      isSkipped,
      isDismissed,
      hasEngaged,
      lastStepIndex,
      setStepIndex,
      markCompleted,
      markSkipped,
      dismissForever,
      resetTour,
      $reset,
    }
  },
  {
    persist: {
      key: TOUR_STORAGE_KEY,
      pick: [
        'completedTourIds',
        'skippedTourIds',
        'dismissedTourIds',
        'stepIndexByTour',
        'trainingModeAllScreens',
      ],
    },
  },
)
